import type { Dispatch, SetStateAction } from "react"
import { useCallback, useRef } from "react"
import { IconOfficeFill18 as OfficeIcon } from "nucleo-ui-fill-18"

import type { CompaniesSelection } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"

export function mergeCompanySelection(
	current: ReadonlySet<string>,
	fetched: readonly string[],
): Set<string> {
	return new Set([...current, ...fetched])
}

interface Args {
	selection: CompaniesSelection
	loadedIds: readonly string[]
	setSelectedIds: Dispatch<SetStateAction<ReadonlySet<string>>>
	enabled: boolean
}

/**
 * The list only syncs the rows around the viewport, so Cmd+A selects those at
 * once and then asks the server for every company the search and filters match.
 */
export function useSelectAllCompanies({
	selection,
	loadedIds,
	setSelectedIds,
	enabled,
}: Args) {
	const utils = api.useUtils()
	const selectionKey = JSON.stringify(selection)
	const latestSelectionKey = useRef(selectionKey)
	latestSelectionKey.current = selectionKey
	const latestRequest = useRef(0)

	const selectAll = useCallback(() => {
		const request = ++latestRequest.current
		const requestedFor = selectionKey
		setSelectedIds((current) => mergeCompanySelection(current, loadedIds))
		utils.company.selectAll
			.fetch(selection)
			.then(({ ids, hasMore }) => {
				// A cleared selection or a new search while loading wins over this result.
				if (latestRequest.current !== request) return
				if (latestSelectionKey.current !== requestedFor) return
				setSelectedIds((current) => mergeCompanySelection(current, ids))
				if (hasMore) {
					toast.info({
						title: `Selected the first ${ids.length.toLocaleString()} companies`,
						description: "Use filters for smaller batches.",
						icon: OfficeIcon,
					})
				}
			})
			.catch((error: unknown) => {
				captureError(error, {
					domain: "general",
					action: "select all companies",
				})
				toast.error({
					title: "Could not select all companies. Try again.",
					icon: OfficeIcon,
				})
			})
	}, [selection, selectionKey, loadedIds, setSelectedIds, utils])

	// Text fields keep the native select-all: useShortcut skips them.
	useShortcut(
		"meta+a, ctrl+a",
		(event) => {
			if (!enabled) return
			event.preventDefault()
			selectAll()
		},
		{ preventDefault: true },
		[selectAll, enabled],
	)

	const cancelSelectAll = useCallback(() => {
		latestRequest.current++
	}, [])

	return { selectAll, cancelSelectAll }
}
